import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Avatar,
    Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadCourse from '../../assets/Upload_Course.png';
import Bulk from '../../assets/Bulk.png';
import ChatGPT from '../../assets/ChatGPT.png';
import Gemini from '../../assets/Gemini.png';
import Copilot from '../../assets/Copilot.png';
import MSTeam from '../../assets/MSTeam-Settings.png';
import ManageMap from '../../assets/ManageMap.jpg';
import Corona from '../../assets/Corona.png';
import { SUBSCRIPTION_PRICING_INFORMATION } from '../../configs/constant';
import { Done } from '@mui/icons-material';

const Details = ({ card }) => (
    <Stack my={2.5} className="space-y-4">
        <div className="details">
            <Typography>Courses Included:</Typography>
            <Typography>{card?.courses}</Typography>
        </div>
        <div className="details">
            <Typography>Upload Quota: </Typography>
            <Typography> {card?.uploadQuota}</Typography>
        </div>
        <div className="details">
            <Typography>Max Package Size: </Typography>
            <Typography>{card?.maxPackageSize}</Typography>
        </div>
        <div className="details">
            <Typography>Media Hours: </Typography>
            <Typography>{card?.mediaHours}</Typography>
        </div>
        <div className="details">
            <Typography>Cost/Course: </Typography>
            <Typography>{card?.costPerCourse}</Typography>
        </div>
    </Stack>
);

const primaryColor = '#4c43a3';
const accentColor = '#26c6da';

const GettingStarted = () => {
    const keyCapabilities = [
        'Upload SCORM 1.2 & 2004 packages',
        'Extract structured course data',
        'Bulk upload via cloud repositories',
        'Preview course content instantly',
        'Enable AI-powered Continuous Learning',
        'Use xTra AI Assistant (Trial users)',
        'Connect with LMS & AI systems via APIs',
        'Manage subscriptions inside the platform',
    ];

    const registrationSteps = [
        { step: 1, description: 'Click Sign Up', icon: PersonAddAlt1RoundedIcon },
        { step: 2, description: 'Enter your business details', icon: BusinessCenterRoundedIcon },
        {
            step: 3,
            description: 'Log in and begin your trial',
            icon: LoginRoundedIcon,
        },
    ];

    const trialFeatures = [
        '1 SCORM upload (Max 256MB)',
        'AI-powered content extraction',
        'Access to xTra AI Assistant (7 questions)',
        'During the trial: Reminders and updates on xTract2AI',
    ];

    const trialEndsFeatures = [
        'Upload and AI features are paused',
        'AI chat history is permanently deleted',
        'You can upgrade anytime to continue',
    ];

    const uploadSteps = [
        { step: 1, title: 'Go to Courses' },
        { step: 2, title: 'Click Upload Course' },
        { step: 3, title: 'Select your SCORM .zip file' },
        { step: 4, title: 'Extraction starts automatically' },
    ];

    const bulkUploadFeatures = [
        'Connect AWS S3, Azure Blob, or Google Cloud Storage',
        'Create upload batches',
        'Process multiple SCORM files at once',
        'Monitor extraction progress in real time',
    ];

    const viewingFeatures = [
        'Course structure',
        'Modules & lessons',
        'Quiz text',
        'Media metadata',
        'Transcripts',
        'Structured PDF, MD & JSON format',
    ];

    const xTraFeatures = [
        'Greet you personally',
        'Answer up to 7 course-related questions',
        'Respond strictly from your uploaded course content',
        'Handle basic conversational queries',
    ];

    const xTraEndFeatures = ['Chat input is disabled', 'You’ll see an upgrade prompt', 'You can subscribe to continue'];

    const continuousLearningSteps = [
        { step: 1, title: 'Select a completed course' },
        { step: 2, title: 'Click the ∞ icon under Actions' },
        { step: 3, title: 'Enter OpenAI API Key and Tenant ID' },
        { step: 4, title: 'Save configuration' },
    ];

    const continuousLearningFeatures = [
        'Course-specific AI assistants',
        'Internal knowledge bots',
        'AI bots trained on proprietary learning content',
        'LMS-integrated AI workflows',
    ];

    const apiFeatures = [
        'Upload courses via API',
        'Retrieve structured JSON',
        'Automate bulk uploads',
        'Trigger AI workflows',
        'Integrate with LMS platforms',
    ];

    const securityFeatures = [
        'Encrypted file transfers',
        'Secure cloud storage',
        'Role-based access control',
        'API authentication tokens',
        'AI chat data deleted after trial expiry',
        'Full activity logging',
    ];

    return (
        <Box sx={{ fontFamily: 'Inter, sans-serif', color: '#000000' }}>
            {/* Header Section */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
                    Welcome to Xtract2AI
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                    Xtract2AI transforms legacy SCORM packages into structured, AI-ready formats like JSON, PDF, and MarkDown
                    (MD). It helps organizations modernize their learning content, power AI assistants, and integrate
                    training data into advanced analytics or LMS ecosystems.
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#000000',
                        lineHeight: 1.8,
                        backgroundColor: '#f8f9fa',
                        padding: '10px 0',
                        borderRadius: '6px',
                        marginTop: '12px',
                        fontStyle: 'italic',
                        fontWeight: 500,
                    }}
                >
                    You don’t just extract SCORM here — you unlock it for the AI era.
                </Typography>
            </Box>

            {/* Key Capabilities Card */}
            <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ px: 4, py: 3 }}>
                    <Typography variant="h5" sx={{ textAlign: 'center', fontWeight: 700, mb: 2, color: '#000' }}>
                        What You Can Do with Xtract2AI
                    </Typography>

                    <List sx={{ py: 0, display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                        {keyCapabilities.map((capability, index) => (
                            <ListItem key={index} sx={{ px: 0, py: 1, flexBasis: { xs: '100%', sm: '50%', md: '33.33%' } }}>
                                <ListItemIcon sx={{ minWidth: '40px' }}>
                                    <CheckCircleIcon sx={{ color: primaryColor }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={capability}
                                    sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </CardContent>
            </Card>

            {/* Account Registration Section */}
            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Create Your Account
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 4 }}>
                    Getting started takes just a few minutes
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mb: 3 }}>
                    {registrationSteps.map((item) => (
                        <Box
                            key={item.step}
                            sx={{
                                flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(20% - 14px)' },
                            }}
                        >
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid #e0e0e0',
                                    transition: 'box-shadow 0.3s',
                                    '&:hover': { boxShadow: '0 8px 30px rgba(76, 67, 163, 0.15)' },
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                                    <Avatar
                                        sx={{
                                            bgcolor: primaryColor,
                                            width: 50,
                                            height: 50,
                                            mx: 'auto',
                                            mb: 2,
                                            fontSize: '1rem',
                                            fontWeight: 700,
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <item.icon sx={{ color: 'white' }} />
                                    </Avatar>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: '16px', fontWeight: 500, color: '#000', lineHeight: 1.7 }}
                                    >
                                        {item.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    ))}
                </Box>
                <Typography variant="body1" sx={{ textAlign: 'center', color: '#6a677e', lineHeight: 1.8 }}>
                    Once registered, your 14-day Trial Plan activates automatically.
                </Typography>
            </Box>

            {/* Trial Plan Activation Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 4, color: '#000000' }}>
                    Your 14-Day Trial Includes
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4 }}>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e0e0e0',
                            mb: 2,
                        }}
                    >
                        <CardContent sx={{ px: 4, py: 3 }}>
                            <List sx={{ py: 0 }}>
                                {trialFeatures.map((feature, index) => (
                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: '40px' }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={feature}
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                            <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, mt: 2 }}>
                                You’ll receive reminders before your trial expires.
                            </Typography>
                        </CardContent>
                    </Card>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            border: '1px solid #e0e0e0',
                            mb: 2,
                        }}
                    >
                        <CardContent sx={{ px: 4, py: 3 }}>
                            <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                                When the trial ends:
                            </Typography>

                            <List sx={{ py: 0 }}>
                                {trialEndsFeatures.map((feature, index) => (
                                    <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: '40px' }}>
                                            <CancelIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={feature}
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Uploading Your First SCORM File */}
            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 4, color: '#000000' }}>
                    Upload Your First SCORM Course
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <Box sx={{ width: { xs: '100%', lg: 'calc(60% - 16px)' } }}>
                        {uploadSteps.map((item) => (
                            <Box key={item.step} sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: primaryColor,
                                        width: 32,
                                        height: 32,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {item.step}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000' }}>
                                        {item.title}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                        <Typography variant="body2" sx={{ color: '#6a677e', mt: 2 }}>
                            Once completed, you can preview your structured course output instantly.
                        </Typography>
                    </Box>
                    <Box sx={{ width: { xs: '100%', lg: 'calc(40% - 16px)' } }}>
                        <img
                            src={UploadCourse}
                            alt="Upload Course"
                            style={{ width: '100%', height: 'auto', borderRadius: 16 }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Bulk Upload Using Repository */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Bulk Upload with Repository
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 4 }}>
                    Managing large content libraries? Use the Repository feature.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <Box sx={{ width: { xs: '100%', lg: 'calc(60% - 16px)' } }}>
                        <Typography variant="body1" sx={{ color: '#6a677e', mt: 2 }}>
                            You can:
                        </Typography>

                        <List sx={{ p: 0, mt: 1 }}>
                            {bulkUploadFeatures.map((feature, index) => (
                                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: primaryColor }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={feature}
                                        sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Typography variant="body1" sx={{ color: '#6a677e', mt: 2 }}>
                            Perfect for LMS migrations and large-scale digital transformation.
                        </Typography>
                    </Box>
                    <Box sx={{ width: { xs: '100%', lg: 'calc(40% - 16px)' } }}>
                        <img
                            src={Bulk}
                            alt="Upload Repository"
                            style={{ width: '100%', height: 'auto', borderRadius: 16 }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Viewing Extracted Content */}
            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Preview Extracted Content
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 4 }}>
                    After extraction, you can view:
                </Typography>
                <List
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}
                >
                    {viewingFeatures.map((feature, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                padding: '16px 20px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                background: '#fff',
                                color: '#000',
                            }}
                        >
                            <ListItemText
                                primary={feature}
                                sx={{
                                    textAlign: 'center',
                                    '& .MuiListItemText-primary': { color: '#000', fontWeight: 500 },
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Typography variant="body1" sx={{ textAlign: 'center', color: '#6a677e', lineHeight: 1.8, mt: 2 }}>
                    Paid plans unlock export and download options.
                </Typography>
            </Box>

            {/* Meet xTra */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Meet xTra – Your AI Learning Assistant (Trial Feature)
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 4 }}>
                    Once your course extraction is complete, xTra becomes available under the Actions section.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    <Box>
                        <Card
                            sx={{
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <CardContent sx={{ px: 4, py: 3 }}>
                                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                                    What xTra Can Do
                                </Typography>
                                <List>
                                    {xTraFeatures.map((feature, index) => (
                                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <CheckCircleIcon sx={{ color: primaryColor }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={feature}
                                                sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                                    Paid plans unlock export and download options.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                    <Box>
                        <Card
                            sx={{
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e0e0e0',
                                height: '100%',
                            }}
                        >
                            <CardContent sx={{ px: 4, py: 3 }}>
                                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                                    After 7 questions:
                                </Typography>

                                <List sx={{ py: 0 }}>
                                    {xTraEndFeatures.map((feature, index) => (
                                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: '40px' }}>
                                                <CancelIcon sx={{ color: primaryColor }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={feature}
                                                sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
                <Typography variant="body1" sx={{ textAlign: 'center', color: '#6a677e', lineHeight: 1.8, mt: 2 }}>
                    AI conversations are stored during your trial and automatically deleted after expiry.
                </Typography>
            </Box>

            {/* Continuous Learning */}
            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Continuous Learning (AI Integration)
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 4 }}>
                    Continuous Learning connects your extracted SCORM content to enterprise AI systems like:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', mb: 4 }}>
                    <img src={ChatGPT} alt="ChatGPT" style={{ height: '50px' }} />
                    <img src={Gemini} alt="Gemini" style={{ height: '50px' }} />
                    <img src={Copilot} alt="Copilot" style={{ height: '50px' }} />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(60% - 12px)' } }}>
                        <Typography variant="body1" sx={{ color: '#000', fontWeight: 600, lineHeight: 1.8, mb: 2 }}>
                            How It Works
                        </Typography>
                        {continuousLearningSteps.map((item) => (
                            <Box key={item.step} sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: primaryColor,
                                        width: 32,
                                        height: 32,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {item.step}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#000000' }}>
                                        {item.title}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                        <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                            Your structured content is securely transferred to your AI environment.
                        </Typography>
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(40% - 12px)' } }}>
                        <img
                            src={MSTeam}
                            alt="MSTeam-Settings"
                            style={{ width: '100%', height: 'auto', borderRadius: 16 }}
                        />
                    </Box>
                </Box>
                <Card
                    sx={{
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #e0e0e0',
                        mb: 2,
                        mt: 4,
                    }}
                >
                    <CardContent sx={{ px: 4, py: 3 }}>
                        <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                            Now your organization can build:
                        </Typography>

                        <List sx={{ py: 0 }}>
                            {continuousLearningFeatures.map((feature, index) => (
                                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: '40px' }}>
                                        <CheckCircleIcon sx={{ color: primaryColor }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={feature}
                                        sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
                <Typography variant="body1" sx={{ textAlign: 'center', color: '#6a677e', lineHeight: 1.8, mb: 3 }}>
                    This feature transforms static SCORM into intelligent, queryable knowledge.
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 3,
                        justifyContent: 'center',
                    }}
                >
                    <img src={ManageMap} alt="Manage Map" style={{ width: '100%', height: 'auto' }} />
                    <img src={Corona} alt="Corona" style={{ width: '100%', height: 'auto' }} />
                </Box>
            </Box>

            {/* API & Integrations */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    API & Integrations
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 3 }}>
                    Xtract2AI offers secure REST APIs and webhooks for automation.
                </Typography>

                <List sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                    {apiFeatures.map((feature, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                padding: '16px 20px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                background: '#fff',
                                color: '#000',
                                width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 16px)' },
                            }}
                        >
                            <ListItemText
                                primary={feature}
                                sx={{
                                    textAlign: 'center',
                                    '& .MuiListItemText-primary': { color: '#000', fontWeight: 500 },
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mt: 2 }}>
                    Designed for developers, L&D teams, and enterprise architects.
                </Typography>
            </Box>

            {/* Upgrade Anytime */}
            <Box sx={{ bgcolor: '#f8f9fa', p: 4, borderRadius: 3, mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Upgrade Anytime
                </Typography>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mb: 3 }}>
                    Go to Subscriptions → Activate Plans
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: { xs: 'wrap', sm: 'wrap', md: 'wrap', lg: 'nowrap' },
                        gap: { xs: 2, sm: 2, md: 2, lg: 1.5 },
                        mt: 3,
                        '& > *': {
                            flex: {
                                xs: '1 1 100%',
                                sm: '1 1 calc(50% - 8px)',
                                md: '1 1 calc(50% - 8px)',
                                lg: '1 1 0',
                            },
                            minWidth: 0,
                            maxWidth: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(50% - 8px)', lg: 'none' },
                        },
                    }}
                >
                    {SUBSCRIPTION_PRICING_INFORMATION?.map((card) => (
                        <Card key={card?.title} className="pricing card-container">
                            <CardContent
                                sx={{ pt: 2, p: '0.875rem !important', '&:last-child': { pb: '0.875rem !important' } }}
                            >
                                <Stack sx={{ mb: 1 }} direction={'column'} justifyContent={'space-around'}>
                                    <div className="text-center">
                                        <Typography className="card-title">{card?.title}</Typography>

                                        <div className="pricing-container">
                                            <Typography className="price">{card?.price}</Typography>
                                            <Typography className="quantity">{card?.priceQuantity}</Typography>
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <Details card={card} />

                                    {card?.info?.map((info) => (
                                        <Stack key={info} className="info">
                                            <Done color="primary" />
                                            <Typography key={info}>{info}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', my: 3 }}>
                    Your plan activates immediately after payment.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 3,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid #e0e0e0',
                            }}
                        >
                            <CardContent sx={{ px: 4, py: 3 }}>
                                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8 }}>
                                    Pro and Enterprise unlock:
                                </Typography>
                                <List sx={{ py: 0 }}>
                                    <ListItem sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Unlimited AI usage (fair usage policy)"
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                    <ListItem sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Full exports"
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                    <ListItem sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Continuous Learning integrations"
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                    <ListItem sx={{ px: 0, py: 0.5 }}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <CheckCircleIcon sx={{ color: primaryColor }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Advanced analytics"
                                            sx={{ '& .MuiListItemText-primary': { color: '#6a677e' } }}
                                        />
                                    </ListItem>
                                </List>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            </Box>

            {/* Security & Data Protection */}
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2, color: '#000000' }}>
                    Security & Data Protection
                </Typography>

                <List
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                        gap: 4,
                    }}
                >
                    {securityFeatures.map((feature, index) => (
                        <ListItem
                            key={index}
                            sx={{
                                padding: '16px 20px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                background: '#fff',
                                color: '#000',
                            }}
                        >
                            <ListItemText
                                primary={feature}
                                sx={{
                                    textAlign: 'center',
                                    '& .MuiListItemText-primary': { color: '#000', fontWeight: 500 },
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
                <Typography variant="body1" sx={{ color: '#6a677e', lineHeight: 1.8, textAlign: 'center', mt: 2 }}>
                    Your content remains protected at every stage.
                </Typography>
            </Box>
        </Box>
    );
};

export default GettingStarted;
